import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import Tour from './Tour';

/* One opener for the tour, reachable from anywhere.
 *
 * There are three ways in and they must all land in the same place: Take Tour in the top bar
 * (the whole thing), a "?" beside a feature, and a `?tour=` URL someone was sent. A context is
 * the cheapest way to let a "?" buried in an item card open the tour without threading a
 * callback through the round editor, the item list and the card.
 */
interface TourApi {
  /** `undefined` runs the whole tour; a step id or chapter name runs that chapter. */
  open: (startAt?: string) => void;
}

const Ctx = createContext<TourApi>({ open: () => {} });

export const useTour = () => useContext(Ctx);

export function TourProvider({ children }: { children: ReactNode }) {
  /* null = closed. A string (possibly empty) = running, from that point. Empty string rather
     than undefined for "the whole tour", so `null` is unambiguously the closed state. */
  const [startAt, setStartAt] = useState<string | null>(null);

  const open = useCallback((at?: string) => setStartAt(at ?? ''), []);
  const api = useMemo(() => ({ open }), [open]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {startAt !== null && (
        /* Keyed so a second "?" while the tour is already open restarts it at the new step,
           rather than leaving the first run's index in place and looking broken. */
        <Tour key={startAt} startAt={startAt || undefined} onClose={() => setStartAt(null)} />
      )}
    </Ctx.Provider>
  );
}
